import { useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';

import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { getAttribution } from '../attribution';
import { useAnalyticsEvents } from '../generated/use-analytics-events';
import { type AuthState, SCROLL_DEPTH_BUCKETS_IN_ORDER, type ScrollDepthBucket } from '../types';

const BUCKET_PERCENTAGE: Record<ScrollDepthBucket, number> = {
	'25': 25,
	'50': 50,
	'75': 75,
	'100': 100,
};

const SCROLL_THROTTLE_MS = 200;

const computeDepthPercent = (): number => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!doc || !win) {
		return 0;
	}
	const scrollTop = win.scrollY ?? doc.documentElement.scrollTop ?? doc.body.scrollTop ?? 0;
	const viewport = win.innerHeight ?? doc.documentElement.clientHeight ?? 0;
	const scrollHeight = doc.documentElement.scrollHeight ?? doc.body.scrollHeight ?? 0;
	const scrollable = Math.max(0, scrollHeight - viewport);
	if (scrollable === 0) {
		// Page fits in the viewport: anything visible is effectively the
		// top of the document. Reporting `100` here would inflate the
		// metric — treat it as "saw the top" only.
		return 0;
	}
	return Math.min(100, Math.max(0, (scrollTop / scrollable) * 100));
};

/**
 * Listens for scroll events on the document and fires
 * `ui.scrollDepth.reached.twgMicrositeHome` exactly once per bucket
 * (25 / 50 / 75 / 100) per page load.
 *
 * Cumulative semantics: scrolling from 0% to 80% in one motion fires
 * 25, 50 AND 75. Scrolling back up does not fire anything.
 *
 * The listener is throttled to a maximum of one fire per
 * `SCROLL_THROTTLE_MS` and disposed once all four buckets have been
 * reached so the hook has zero ongoing cost on heavy pages.
 *
 * Mount this once per page (e.g. from the home `<Page>` shell). It is
 * a no-op outside the browser (SSR, tests without `window`).
 */
export const useScrollDepthEvent = (): void => {
	const { fireEvent } = useAnalyticsEvents();
	const { isAuthed } = useAtlassianUserContext();
	const authState: AuthState = isAuthed ? 'loggedIn' : 'loggedOut';

	const firedBucketsRef = useRef<Set<ScrollDepthBucket>>(new Set());
	const authStateRef = useRef(authState);
	useEffect(() => {
		authStateRef.current = authState;
	}, [authState]);

	useEffect(() => {
		const win = getDocument()?.defaultView;
		if (!win) {
			return;
		}

		let throttleHandle: ReturnType<typeof setTimeout> | null = null;
		let pending = false;
		let unbindScroll: (() => void) | null = null;

		const onScroll = (): void => {
			if (throttleHandle !== null) {
				pending = true;
				return;
			}
			throttleHandle = setTimeout(() => {
				throttleHandle = null;
				if (pending) {
					pending = false;
					onScroll();
				}
			}, SCROLL_THROTTLE_MS);
			const depthPercent = computeDepthPercent();
			for (const bucket of SCROLL_DEPTH_BUCKETS_IN_ORDER) {
				if (firedBucketsRef.current.has(bucket)) {
					continue;
				}
				if (depthPercent >= BUCKET_PERCENTAGE[bucket]) {
					firedBucketsRef.current.add(bucket);
					fireEvent('ui.scrollDepth.reached.twgMicrositeHome', {
						depthBucket: bucket,
						authState: authStateRef.current,
						...getAttribution(),
					});
				}
			}
			if (firedBucketsRef.current.size === SCROLL_DEPTH_BUCKETS_IN_ORDER.length && unbindScroll) {
				unbindScroll();
				unbindScroll = null;
			}
		};

		unbindScroll = bind(win, {
			type: 'scroll',
			listener: onScroll,
			options: { passive: true },
		});
		// Fire-once on mount in case the page was already scrolled (e.g.
		// reload-with-scroll-restoration).
		onScroll();
		return (): void => {
			if (unbindScroll) {
				unbindScroll();
			}
			if (throttleHandle !== null) {
				clearTimeout(throttleHandle);
			}
		};
	}, [fireEvent]);
};
